/**
 * Phase 1 helpers for Match invoice: always let the user add extra POs.
 * Combined three-way match is later; extra POs continue into Allocate.
 */

let extraOrderRowKey = 0

export function createMatchExtraOrderRow(overrides = {}) {
  extraOrderRowKey += 1
  return {
    key: `match-extra-order-${extraOrderRowKey}`,
    purchaseOrderNumber: '',
    linkedOrderId: null,
    orderedDate: null,
    deliveryDate: null,
    sentToXero: false,
    status: '',
    ...overrides,
  }
}

export function shouldShowAddAnotherOrderInMatchModal({
  isCreditNote = false,
} = {}) {
  return !isCreditNote
}

export function getFilledMatchExtraOrders(extraRows = []) {
  return (extraRows || []).filter(row =>
    String(row?.purchaseOrderNumber || '').trim(),
  )
}

export function isMatchMultiOrderDraft(extraRows = []) {
  return getFilledMatchExtraOrders(extraRows).length > 0
}

export function collectMatchSelectedOrders({
  purchaseOrderNumber,
  linkedOrderId,
  extraRows = [],
} = {}) {
  const selected = []
  const seen = new Set()

  const push = (po, orderId, extras = {}) => {
    const purchaseOrder = String(po || '').trim()
    if (!purchaseOrder) return
    const key = purchaseOrder.toUpperCase()
    if (seen.has(key)) return
    seen.add(key)
    selected.push({
      purchaseOrderNumber: purchaseOrder,
      linkedOrderId: orderId || extras.linkedOrderId || null,
      sentToXero: Boolean(extras.sentToXero),
    })
  }

  push(purchaseOrderNumber, linkedOrderId)
  for (const row of extraRows || []) {
    push(row.purchaseOrderNumber, row.linkedOrderId, row)
  }

  return selected
}

export function getDuplicateMatchPoMessage(candidatePo, selectedPos = []) {
  const po = String(candidatePo || '')
    .trim()
    .toUpperCase()
  if (!po) return null
  const alreadyListed = (selectedPos || []).some(
    value =>
      String(value || '')
        .trim()
        .toUpperCase() === po,
  )
  return alreadyListed ? 'This purchase order is already on the list.' : null
}

export function getSentToXeroMatchWarning(row = {}) {
  if (!row?.sentToXero) return null
  return 'This order is already sent to Xero. Check it is not already reconciled against another invoice before continuing.'
}

/**
 * Seed Match modal state from saved multi-PO allocations or engine mirrors.
 */
export function buildMatchOrdersFromInvoice(invoice = {}) {
  const allocationRows = Array.isArray(invoice.poAllocations)
    ? invoice.poAllocations.filter(row =>
        String(row?.purchaseOrderNumber || '').trim(),
      )
    : []

  if (allocationRows.length >= 2) {
    const [primary, ...rest] = allocationRows
    return {
      purchaseOrderNumber: String(primary.purchaseOrderNumber || '').trim(),
      linkedOrderId: primary.orderId ? String(primary.orderId) : null,
      extraOrders: rest.map(row =>
        createMatchExtraOrderRow({
          purchaseOrderNumber: String(row.purchaseOrderNumber || '').trim(),
          linkedOrderId: row.orderId ? String(row.orderId) : null,
        }),
      ),
      source: 'allocations',
    }
  }

  const matchedPos = Array.isArray(invoice.matchedPurchaseOrderNumbers)
    ? invoice.matchedPurchaseOrderNumbers
        .map(value => String(value || '').trim())
        .filter(Boolean)
    : []

  if (matchedPos.length >= 2) {
    const matchedOrderIds = Array.isArray(invoice.matchedOrderIds)
      ? invoice.matchedOrderIds.map(id => String(id || '')).filter(Boolean)
      : []
    const [primaryPo, ...restPos] = matchedPos
    return {
      purchaseOrderNumber: primaryPo,
      linkedOrderId: invoice.linkedOrderId
        ? String(invoice.linkedOrderId)
        : matchedOrderIds[0] || null,
      // Pair each extra PO with the matching matchedOrderIds slot (index + 1).
      // Do not reuse linkedOrderId / matchedOrderIds[0] — that caused
      // purchaseOrderNumber vs order mismatches when correcting a dual match.
      extraOrders: restPos.map((po, index) =>
        createMatchExtraOrderRow({
          purchaseOrderNumber: po,
          linkedOrderId: matchedOrderIds[index + 1] || null,
        }),
      ),
      source: 'matched',
    }
  }

  return null
}
